/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  Floranza — Pre-Order Cart Guard  (v3)
 * ─────────────────────────────────────────────────────────────────────────────
 *  Blocks checkout whenever the cart contains BOTH Pre-Order items and
 *  regular items at the same time.
 *
 *  Detection order:
 *    1. sections/preorder-cart-state.liquid  → reads the product metafield
 *       (accurate even for permalink / Shop app / abandoned-cart carts)
 *    2. /cart.js line item property `_dispatch_badge`  → fallback only
 *
 *  Works on: /cart page, cart drawer, and anywhere a checkout button exists.
 *  No Horizon section files need to be edited.
 *
 *  v3 fixes an infinite render loop: the MutationObserver is now suspended
 *  while we mutate the DOM, DOM writes are idempotent, and observer callbacks
 *  are debounced.
 * ─────────────────────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  // ── Config ────────────────────────────────────────────────────────────────

  var PREORDER_VALUE = 'Pre-Order';           // exact metafield value
  var STATE_SECTION  = 'preorder-cart-state'; // sections/preorder-cart-state.liquid
  var BODY_CLASS     = 'cpg-cart-blocked';    // added to <body> when mixed
  var WARNING_TEXT   = 'Pre-Order items must be ordered separately. Remove one type to continue.';
  var TOAST_TEXT     = 'Remove Pre-Order or regular items to checkout.';

  var CHECKOUT_SELECTOR = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    '[data-testid="checkout-button"]',
    '.cart__checkout-button',
    'a[href="/checkout"]',
    'a[href^="/checkout?"]'
  ].join(',');

  // ── State ─────────────────────────────────────────────────────────────────

  var isMixed      = false;  // does the cart currently mix the two types?
  var isApplying   = false;  // re-entrancy guard for applyState()
  var observer     = null;
  var toastTimer   = null;
  var refreshTimer = null;
  var observerTimer = null;

  // ── Cart inspection ───────────────────────────────────────────────────────

  /**
   * PRIMARY CHECK — asks Liquid.
   * Renders the helper section, which reads the product metafield directly.
   * @returns {Promise<boolean|null>} null = unavailable, caller should fall back
   */
  function cartIsMixedViaSection() {
    return fetch('/cart?section_id=' + STATE_SECTION, { cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('section fetch failed');
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var el  = doc.getElementById('cpg-cart-state');
        if (!el) return null;

        var preOrderCount = parseInt(el.getAttribute('data-preorder-count'), 10) || 0;
        var regularCount  = parseInt(el.getAttribute('data-regular-count'), 10) || 0;

        return preOrderCount > 0 && regularCount > 0;
      })
      .catch(function () {
        return null;
      });
  }

  /**
   * FALLBACK CHECK — line item properties from /cart.js.
   * @returns {Promise<boolean>}
   */
  function cartIsMixedViaProperties() {
    return fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var preOrderCount = 0;
        var regularCount  = 0;

        (cart.items || []).forEach(function (item) {
          var badge = item.properties && item.properties._dispatch_badge;
          if (badge === PREORDER_VALUE) preOrderCount++;
          else regularCount++;
        });

        return preOrderCount > 0 && regularCount > 0;
      })
      .catch(function () {
        return false; // never block because of a network hiccup
      });
  }

  function cartIsMixed() {
    return cartIsMixedViaSection().then(function (result) {
      return result !== null ? result : cartIsMixedViaProperties();
    });
  }

  // ── DOM writes (all idempotent — safe to call repeatedly) ─────────────────

  /**
   * Builds the warning icon + text markup.
   */
  function noticeMarkup(text) {
    return (
      '<svg viewBox="0 0 24 24" aria-hidden="true">' +
      '<circle cx="12" cy="12" r="10" fill="#e53e3e"/>' +
      '<rect x="11" y="6" width="2" height="8" rx="1" fill="#fff"/>' +
      '<rect x="11" y="16" width="2" height="2" rx="1" fill="#fff"/>' +
      '</svg><span>' + text + '</span>'
    );
  }

  /**
   * Ensures exactly one notice sits above each checkout button when blocked,
   * and none when not. Does nothing if the DOM already matches — this is what
   * stops the observer feedback loop.
   */
  function syncInlineNotice() {
    if (!isMixed) {
      document.querySelectorAll('.cpg-notice').forEach(function (el) {
        el.remove();
      });
      return;
    }

    document.querySelectorAll(CHECKOUT_SELECTOR).forEach(function (btn) {
      var anchor =
        btn.closest('.cart__ctas, .cart-drawer__footer, .cart__footer, form') ||
        btn.parentElement;

      if (!anchor) return;

      // Already has one → leave it alone (no mutation, no loop)
      if (anchor.querySelector('.cpg-notice')) return;

      var notice = document.createElement('div');
      notice.className = 'cpg-notice';
      notice.setAttribute('role', 'alert');
      notice.innerHTML = noticeMarkup(WARNING_TEXT);

      anchor.insertBefore(notice, anchor.firstChild);
    });
  }

  /**
   * Reflects `isMixed` onto the DOM. The observer is suspended for the
   * duration so our own writes cannot retrigger it.
   */
  function applyState() {
    if (isApplying) return;
    isApplying = true;

    if (observer) observer.disconnect();

    try {
      if (document.body.classList.contains(BODY_CLASS) !== isMixed) {
        document.body.classList.toggle(BODY_CLASS, isMixed);
      }

      document.querySelectorAll(CHECKOUT_SELECTOR).forEach(function (btn) {
        var isNative = btn.tagName === 'BUTTON' || btn.tagName === 'INPUT';

        if (isMixed) {
          if (btn.classList.contains('cpg-disabled')) return; // already done
          if (isNative) btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
          btn.classList.add('cpg-disabled');
        } else if (btn.classList.contains('cpg-disabled')) {
          // Only re-enable buttons WE disabled
          if (isNative) btn.disabled = false;
          btn.removeAttribute('aria-disabled');
          btn.classList.remove('cpg-disabled');
        }
      });

      syncInlineNotice();
    } finally {
      // Reconnect on the next frame so our mutations have already flushed
      requestAnimationFrame(function () {
        isApplying = false;
        startObserving();
      });
    }
  }

  /**
   * Re-reads the cart, then re-applies. Debounced.
   */
  function refresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      cartIsMixed().then(function (mixed) {
        isMixed = mixed;
        applyState();
      });
    }, 100);
  }

  // ── Toast ─────────────────────────────────────────────────────────────────

  function showToast() {
    var banner = document.querySelector('.vp-cart-warning-banner');

    if (!banner) {
      banner = document.createElement('div');
      banner.className = 'vp-cart-warning-banner';
      document.body.appendChild(banner);
    }

    banner.innerHTML = noticeMarkup(TOAST_TEXT);
    banner.classList.add('is-visible');

    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      banner.classList.remove('is-visible');
    }, 4000);
  }

  // ── Hard guards (capture phase — run before the theme's handlers) ─────────

  document.addEventListener(
    'click',
    function (e) {
      if (!isMixed) return;
      var target = e.target.closest && e.target.closest(CHECKOUT_SELECTOR);
      if (!target) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      showToast();
    },
    true
  );

  document.addEventListener(
    'submit',
    function (e) {
      if (!isMixed) return;

      var form   = e.target;
      var action = (form.getAttribute('action') || '').toLowerCase();
      var goesToCheckout =
        action.indexOf('/checkout') !== -1 ||
        (e.submitter && e.submitter.name === 'checkout') ||
        !!form.querySelector('[name="checkout"]');

      if (!goesToCheckout) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      showToast();
    },
    true
  );

  // ── Observer (debounced, and suspended during our own writes) ─────────────

  function startObserving() {
    if (!observer) {
      observer = new MutationObserver(function () {
        if (isApplying) return;

        // Re-read the cart rather than replaying the previous state.
        // Replaying is what caused a removed item to leave the warning
        // stuck on screen: the DOM re-render fired the observer, which
        // re-applied `isMixed` from before the removal.
        clearTimeout(observerTimer);
        observerTimer = setTimeout(function () {
          refresh();
        }, 200);
      });
    }

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ── Boot ──────────────────────────────────────────────────────────────────

  function start() {
    refresh();
    startObserving();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('cart:update', refresh);
  document.addEventListener('cart:refresh', refresh);
  document.addEventListener('cart:change', refresh);
  document.addEventListener('cart:item-removed', refresh);
  window.addEventListener('pageshow', refresh);
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) refresh();
  });
})();